/**
 * VendedoresSinTelefonoAlert — banner PERMANENTE + modal para admins.
 *
 * Problema que resuelve: un vendedor sin teléfono cargado SIGUE recibiendo leads
 * (decisión de producto), pero el aviso de WhatsApp nunca sale — y hoy nadie se
 * entera. En Del Parque son 7 de 9 vendedores.
 *
 * Vive en /team-config (hub de empresa, visible a los admins de las 5 verticales).
 * NUNCA en ProductsPage, que está oculta para autos y desarrollos — CLAUDE.md §3 #7b.
 *
 * Endpoint: GET /api/team/vendors-without-phone → { count, vendors:[{id,name}] }
 *   - 403 si no es admin  → el componente NO gatea por rol: se apoya en el backend
 *     (única autoridad) y simplemente no renderiza nada si falla.
 *   - count === 0 → NO renderiza NADA (no molestar a quien lo tiene bien).
 *
 * Banner permanente, no auto-modal: un popup que salta en cada visita se cierra por
 * reflejo; un banner que no se va hasta que lo arreglen sí se atiende.
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle } from 'lucide-react';

export default function VendedoresSinTelefonoAlert({ onGoToTeam }) {
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // 403 (no admin) o error → lista vacía → no se pinta nada.
    api.get('/team/vendors-without-phone')
      .then(r => setVendors(r.data?.vendors || []))
      .catch(() => setVendors([]));
  }, []);

  if (vendors.length === 0) return null;

  const n = vendors.length;
  const isSingular = n === 1;

  return (
    <>
      <div
        className="flex items-center justify-between gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30"
        data-testid="vendors-without-phone-banner"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
          <p className="text-xs text-amber-200">
            {isSingular
              ? '1 vendedor no recibe el aviso de WhatsApp: no tiene teléfono cargado.'
              : `${n} vendedores no reciben el aviso de WhatsApp: no tienen teléfono cargado.`}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="shrink-0 border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
          onClick={() => setOpen(true)}
          data-testid="vendors-without-phone-open"
        >
          Ver quienes
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="vendors-without-phone-modal">
          <DialogHeader>
            <DialogTitle>Vendedores sin telefono</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Estos vendedores si reciben leads, pero como no tienen telefono cargado, no les llega
            el aviso por WhatsApp. Cargales su numero para que se enteren al instante.
          </p>
          <ul className="space-y-1.5 max-h-60 overflow-y-auto py-1" data-testid="vendors-without-phone-list">
            {vendors.map(v => (
              <li
                key={v.id}
                className="text-xs text-white px-3 py-2 rounded-md bg-white/[0.03] border border-white/5"
              >
                {v.name}
              </li>
            ))}
          </ul>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)} data-testid="vendors-without-phone-close">
              Cerrar
            </Button>
            <Button
              size="sm"
              onClick={() => { setOpen(false); onGoToTeam?.(); }}
              data-testid="vendors-without-phone-goto-team"
            >
              Ir al equipo
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
